
import { ApolloQueryResult } from '@apollo/client';
import { IProps } from '@components/messages/types';
import React, { useEffect, useState } from 'react';
import { ITableActions } from 'apps/things/app/item/context/ItemProvider';

import {
  GetAllPayPlanTemplates, 
  GetAllPayPlanTemplatesVariables, 
  GetAllPayPlanTemplates_getAllPayPlanTemplates_page_edges
} from "../types/GetAllPayPlanTemplates"
import { useLazyGetAllPayPlanTemplatesQuery } from '../queries';
import { PAGE_LIST_LIMIT } from '@constants';
import useOVSPagination from 'hooks/useOVSPagination';
import { useDebouncedEffect } from 'utils/useDebouncedEffect';
export interface IPayPlanTemplateContext {
  items: GetAllPayPlanTemplates_getAllPayPlanTemplates_page_edges[];
  setItems: (item: GetAllPayPlanTemplates_getAllPayPlanTemplates_page_edges[]) => void;
  loading: boolean;
  getItems: () => void;
  activeStage: number;
  setActiveStage: (stage: number) => void;
  tableActions: ITableActions[];
  refetchItems:
    | ((
        variables?: Partial<GetAllPayPlanTemplatesVariables> | undefined
      ) => Promise<ApolloQueryResult<GetAllPayPlanTemplates>>)
    | undefined;
  goTo: (nextPrev: boolean) => void;
  paging: any;
  setSearchQuery: (query: string) => void;
  searchQuery: string | undefined;

}

export const PayPlanTemplateContext = React.createContext<IPayPlanTemplateContext>(
  {} as IPayPlanTemplateContext
);

const PayPlanTemplateProvider: React.FC<IProps> = ({ children }) => {
  const [activeStage, setActiveStage] = useState(0);
  const [item, setItem] = React.useState(
    {} as GetAllPayPlanTemplates_getAllPayPlanTemplates_page_edges
  );
  const [items, setItems] = React.useState<
    GetAllPayPlanTemplates_getAllPayPlanTemplates_page_edges[]
  >([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined);
  /// hooks
  const [
    getItems,
    { refetch: refetchItems, data, loading, fetchMore },
  ] = useLazyGetAllPayPlanTemplatesQuery({
    first: PAGE_LIST_LIMIT,
  });

  const {
    endCursorStack,
    initPagAction,
    setPaging,
    paging,
  } = useOVSPagination();

  useEffect(() => {
    setItems(data?.getAllPayPlanTemplates?.page?.edges || []);
    setPaging({
      ...data?.getAllPayPlanTemplates?.pageData,
      ...data?.getAllPayPlanTemplates?.page?.pageInfo,
    }); // eslint-disable-next-line
  }, [data]);

  const goTo = async (next = true) => {
    if (isLoadingMore) return;

    let variables: GetAllPayPlanTemplatesVariables = {
      first: PAGE_LIST_LIMIT,
      ...initPagAction(next),
    };

    if (fetchMore) {
      setIsLoadingMore(true);
      const _data = await fetchMore({
        variables,
        updateQuery: (previousResult, { fetchMoreResult }) => {
          setIsLoadingMore(false);
          if (!fetchMoreResult) {
            return previousResult;
          }
          return {
            ...fetchMoreResult,
          };
        },
      });

      setItems(_data?.data?.getAllPayPlanTemplates?.page?.edges || []);

      setPaging({
        ..._data?.data?.getAllPayPlanTemplates?.pageData,
        ..._data?.data?.getAllPayPlanTemplates?.page?.pageInfo,
        hasPreviousPage: endCursorStack.length > 0,
      });
    }
  };
  const search = async () => {
    if (searchQuery === undefined) {
      return;
    }

    const variables: GetAllPayPlanTemplatesVariables = {
      first: PAGE_LIST_LIMIT,
    };

    setIsLoadingMore(true);
    if (fetchMore) {
      const _data = await fetchMore({
        variables,
        updateQuery: (previousResult, { fetchMoreResult }) => {
          setIsLoadingMore(false);
          if (!fetchMoreResult) {
            return previousResult;
          }
          return {
            ...fetchMoreResult,
          };
        },
      });

      setItems(_data?.data?.getAllPayPlanTemplates?.page?.edges || []);

      setPaging({
        ..._data?.data?.getAllPayPlanTemplates?.pageData,
        ..._data?.data?.getAllPayPlanTemplates?.page?.pageInfo,
      });
    }

  }


  useDebouncedEffect(search, [searchQuery], 1000);

  const value = React.useMemo(
    () => ({
      item,
      setItem,
      items,
      setItems,
      goTo,
      paging,
      loading: loading,
      getItems: getItems,
      refetchItems,
      searchQuery,
      setSearchQuery,
      tableActions: [],
      activeStage,
      setActiveStage,
    }), // eslint-disable-next-line
    [items, loading, paging, searchQuery]
  );

  return (
    <PayPlanTemplateContext.Provider value={value}>{children}</PayPlanTemplateContext.Provider>
  );
};

export default PayPlanTemplateProvider;
